import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const CALCULATOR_LINKS = [
  { path: "/car-payment-calculator", label: "Car Payment Calculator" },
  { path: "/car-loan-calculator", label: "Car Loan Calculator" },
  { path: "/car-affordability-calculator", label: "Car Affordability Calculator" },
  { path: "/dti-calculator", label: "DTI Calculator" },
  { path: "/car-cost-of-ownership", label: "Cost of Ownership" },
];

const NAV_LINKS = [
  { href: "/#how-it-works", label: "How It Works", testid: "nav-how-it-works" },
  { href: "/learn", label: "Learn", testid: "nav-learn" },
  { href: "/about", label: "About", testid: "nav-about" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const calcRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (calcRef.current && !calcRef.current.contains(e.target)) setCalcOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  return (
    <header data-testid="global-header" className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="DriveClear home" className="flex items-center">
          <img src="/logo.png" alt="DriveClear" className="h-8 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-stone-600" aria-label="Primary">
          <div className="relative" ref={calcRef}>
            <button
              type="button"
              data-testid="nav-calculators-dropdown"
              aria-expanded={calcOpen}
              onClick={() => setCalcOpen(!calcOpen)}
              className="inline-flex items-center gap-1 hover:text-blue-700 transition-colors"
            >
              Calculators <ChevronDown className={`w-4 h-4 transition-transform ${calcOpen ? "rotate-180" : ""}`} />
            </button>
            {calcOpen && (
              <div data-testid="nav-calculators-menu" className="absolute left-0 top-full mt-2 w-72 rounded-2xl bg-white border border-stone-200 shadow-xl p-2 z-50">
                {CALCULATOR_LINKS.map((c) => (
                  <Link
                    key={c.path}
                    to={c.path}
                    data-testid={`nav-calc-${c.path.slice(1)}`}
                    onClick={() => setCalcOpen(false)}
                    className="block px-4 py-2.5 rounded-xl text-sm text-stone-700 hover:bg-stone-50 hover:text-blue-700 transition-colors"
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {NAV_LINKS.map((l) => (
            l.href.startsWith("/#") ? (
              <a key={l.testid} href={l.href} data-testid={l.testid} className="hover:text-blue-700 transition-colors">{l.label}</a>
            ) : (
              <Link key={l.testid} to={l.href} data-testid={l.testid} className="hover:text-blue-700 transition-colors">{l.label}</Link>
            )
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href="/#get-app" data-testid="nav-open-driveclear" className="hidden sm:inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition-colors">
            Open DriveClear
          </a>
          <button
            type="button"
            data-testid="nav-mobile-menu-button"
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl border border-stone-200 text-stone-700"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-stone-200 bg-white px-4 py-4 space-y-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-stone-400 px-3 pt-1 pb-2">Calculators</div>
          {CALCULATOR_LINKS.map((c) => (
            <Link key={c.path} to={c.path} data-testid={`nav-calc-${c.path.slice(1)}-mobile`} onClick={() => setOpen(false)} className="block px-3 py-3 rounded-xl text-base font-medium text-stone-700 hover:bg-stone-50">
              {c.label}
            </Link>
          ))}
          <div className="border-t border-stone-100 my-2" />
          {NAV_LINKS.map((l) => (
            l.href.startsWith("/#") ? (
              <a key={l.testid} href={l.href} data-testid={`${l.testid}-mobile`} onClick={() => setOpen(false)} className="block px-3 py-3 rounded-xl text-base font-medium text-stone-700 hover:bg-stone-50">{l.label}</a>
            ) : (
              <Link key={l.testid} to={l.href} data-testid={`${l.testid}-mobile`} onClick={() => setOpen(false)} className="block px-3 py-3 rounded-xl text-base font-medium text-stone-700 hover:bg-stone-50">{l.label}</Link>
            )
          ))}
          <a href="/#get-app" data-testid="nav-open-driveclear-mobile" onClick={() => setOpen(false)} className="block mt-2 px-3 py-3.5 rounded-full text-center text-base font-semibold text-white bg-blue-600">
            Open DriveClear
          </a>
        </div>
      )}
    </header>
  );
}
